import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ICheckoutPayload, ISessionKey } from '@share/common-types';
import { ActivityCheckoutAPI } from '@share/api';
import { Toaster, confirmPaymentWithEmbeddedNextActions, isSpanish, isStripeDirectConfirmMethod, isStripeExpressAuthorized, getSessionObj, UrlUtils, axiosInstance, getHeaders } from '@share/utils';
import type { StripeDirectConfirm } from '@share/utils';
import { Stripe, StripeElement } from '@stripe/stripe-js';
import { Routes, SESSION_KEY_LABEL, SESSION_KEY_LABEL_ROOMS, Urls } from '@share/constants';
import { isString } from 'lodash';
import { CHECK_OUT_PAYMENT_ERROR } from '@constants';

// A classified payment decline arrives with the readable, translated detail in errors[],
// while reason carries only the prefix. Hotels never needs this because the BFF joins the
// two before the response leaves, but an activities checkout is re-emitted verbatim, so we
// join them here to read identically. Same separator the BFF uses.
// Only the checkout payment failure is joined, and when errors[] is absent or empty the
// reason is returned untouched, so every other message stays exactly as it was.
const withPaymentErrorDetail = (data: any, reason: string): string => {
    if (!reason || data?.code !== CHECK_OUT_PAYMENT_ERROR) {
        return reason;
    }

    const details = (data?.errors ?? [])
        .map((item: any) => item?.message)
        .filter((message: string) => !!message);

    return details.length ? `${reason}: ${details.join(', ')}` : reason;
};

export interface IActivityCheckoutState {
    loading: boolean;
    error: string | null;
    errorMessages: string | null;
    success: boolean;
    bookingResult: any | null;
    /** Email from checkout contact form (last successful booking); used on confirmation screen */
    confirmationHolderEmail: string | null;
    bookingAnswers: Record<string, { value: any; unit?: string }>;
    travelerAnswers: Array<Record<string, { value: any; unit?: string }>>;
    selectedActivityReviewClientCash: number;
    selectedActivityReviewClientCashStr: string;
    rewardId: number | null;
}

const initialState: IActivityCheckoutState = {
    loading: false,
    error: null,
    errorMessages: null,
    success: false,
    bookingResult: null,
    confirmationHolderEmail: null,
    bookingAnswers: {},
    travelerAnswers: [],
    selectedActivityReviewClientCash: 0,
    selectedActivityReviewClientCashStr: '',
    rewardId: null,
};

interface ConfirmPaymentPayload {
    clientSecret: string;
    stripe: Stripe;
    elements: StripeElement;
    bookingGuid: string;
    /** Wallet payment description ("Amazon pay" / "Google pay" / ...) to persist on the booking payment. */
    paymentDescription?: string;
    /** Set for Afterpay/Clearpay and Cash App Pay, which are confirmed through their own Stripe API
     *  rather than through Elements (no ExpressCheckoutElement button exists for them). */
    directConfirm?: StripeDirectConfirm;
}

interface GetBookingStripePayload {
    bookingGuid: string;
    sessionKey: ISessionKey;
    paymentMethod?: string;
    isCancel?: boolean;
}

export const confirmActivityPayment = createAsyncThunk(
    'payment/confirmActivityPayment',
    async ({ clientSecret, stripe, elements, bookingGuid, paymentDescription, directConfirm }: ConfirmPaymentPayload, { dispatch, getState, rejectWithValue }) => {
        try {
            if (!stripe || !elements) {
                return rejectWithValue(
                    'Stripe has not initialized yet'
                );
            }
            const state: any = getState();
            const accountUsername = state.loginStore?.account?.name;
            const returnUrl = `${window.location.origin}/${accountUsername}${Routes.ActivityCheckoutExpressResume}${location.search}`;
            const values = UrlUtils.getValues();
            const sessionKey =  values[SESSION_KEY_LABEL] as ISessionKey;
            console.log(clientSecret);
            console.log(bookingGuid);
            const { paymentIntent, error } =
                await confirmPaymentWithEmbeddedNextActions(
                    stripe,
                    {
                        elements,
                        clientSecret,
                        returnUrl,
                        paymentMethodType: directConfirm?.paymentMethodType,
                        billingDetails: directConfirm?.billingDetails,
                    }
                );
            // Authorized (normally `requires_capture` under manual capture): proceed to resume, which books
            // with the provider and then captures (or voids on failure). See `isStripeExpressAuthorized`
            // for the statuses that count — redirect / QR methods (Klarna, Afterpay, Cash App Pay) can also
            // land on `processing`, and treating that as a failure would void a good authorization.
            if (isStripeExpressAuthorized(paymentIntent?.status)) {
                dispatch(setStripeError(''));
                const bookingAction = await dispatch(getActivityBookingStripe({ bookingGuid, sessionKey, paymentMethod: paymentDescription }));
                if (getActivityBookingStripe.fulfilled.match(bookingAction)) {
                    return bookingAction.payload;
                }
                return rejectWithValue((bookingAction.payload as string) || 'Failed to fetch booking status');
            }
            // Not authorized — and BOTH shapes that land here have to cancel.
            //
            // A hard decline surfaces an `error`. A declined Cash App Pay ("Fail") does not: it resolves
            // with no `error` at all, leaving the intent in `requires_payment_method` with
            // `last_payment_error` set. That second shape used to fall through to a bare reject with no
            // backend call, so nothing ever closed the booking — it stayed Unconfirmed with the payment
            // in PendingExpress, the PaymentIntent open and the supplier hold live until it expired
            // (TRAV23-4886). Resuming with `isCancel` voids the intent and closes the booking, which is
            // what rsi has always done for the same scenario.
            //
            // This has to run promptly: the BFF rejects the resume with `BookingNotPendingCapture` once
            // the charge is no longer `PendingExpress`, so it cannot be deferred or retried later.
            const failureMessage =
                error?.message ??
                (paymentIntent as any)?.last_payment_error?.message ??
                'Payment was not completed. Please try again.';

            dispatch(setStripeError(failureMessage));

            const cancelAction = await dispatch(
                getActivityBookingStripe({
                    bookingGuid,
                    sessionKey,
                    paymentMethod: paymentDescription,
                    isCancel: true,
                }),
            );

            // Reject either way: the cancel having succeeded does not make this a successful booking,
            // and the caller needs the rejection to surface the error to the customer.
            return rejectWithValue(
                getActivityBookingStripe.fulfilled.match(cancelAction)
                    ? failureMessage
                    : (cancelAction.payload as string) || failureMessage,
            );

        } catch (error: any) {
            const values = UrlUtils.getValues();

            const sessionKey = getSessionObj(
                values[SESSION_KEY_LABEL_ROOMS] as ISessionKey,
                values[SESSION_KEY_LABEL] as ISessionKey,
            );

            const err = typeof error === 'string'
                ? error
                : error?.message || error?.response?.data || 'Payment failed';

            dispatch(
                setStripeError(err)
            );

            await dispatch(
                getActivityBookingStripe({
                    bookingGuid,
                    sessionKey,
                    isCancel: true
                })
            );

            dispatch(
                handleErrorBookingComplete(error)
            );

            return rejectWithValue(err);
        }
    }
);

export const getActivityBookingStripe = createAsyncThunk(   
'booking/getActivityBookingStripe',
    async ( { bookingGuid,sessionKey, paymentMethod,isCancel = false }: GetBookingStripePayload, { dispatch, rejectWithValue }) => {
        try {
            const payload = {bookingId: bookingGuid,  
            isCancel,
                sessionId: sessionKey.value,
                paymentDescripcion: paymentMethod,
            };
            const { data } = await axiosInstance.get(Urls.ActivityBookExpressResume,
                {
                    ...getHeaders(),
                    params: payload,
                }
            );
            dispatch(
                handleProcessBookingComplete(data)
            );
            return data;
        } catch (err: any) {
            const errorObject = isString(err) ? JSON.parse(err) : err;
            const data = errorObject.response?.data;
            const dataObject = data && isString(data) ? JSON.parse(data) : data;
            const message = withPaymentErrorDetail(dataObject, dataObject?.reason) ?? errorObject.message ??'Unknown error';
            dispatch(setStripeError(message));
            dispatch(handleErrorBookingComplete(err));
            return rejectWithValue(message);
        }
    }
);

export const createActivityBooking = createAsyncThunk(
    'activityCheckout/createActivityBooking',
    async ({ payload, stripe, elements, paymentDescription }: { payload: ICheckoutPayload; stripe: Stripe ; elements: StripeElement; paymentDescription?: string; }, { rejectWithValue, dispatch }) => {
        try {
            const response = await ActivityCheckoutAPI.createBooking(payload);
            if (!response.data) {
                return rejectWithValue(
                    'Invalid response from server'
                );
            }
            
            if ( response.data.bookingStatus == 'InProgress' ) {
                console.log(response.data);
                // Afterpay / Cash App Pay are confirmed through their own Stripe API, which builds the
                // PaymentMethod from billing details rather than from an Element. The holder block the
                // booking was just created with is exactly that data, so reuse it.
                const directConfirm: StripeDirectConfirm | undefined = isStripeDirectConfirmMethod(
                    payload.stripePaymentMethodType,
                )
                    ? {
                        paymentMethodType: payload.stripePaymentMethodType as string,
                        billingDetails: {
                            name: [payload.holder?.givenName, payload.holder?.surName]
                                .filter(Boolean)
                                .join(' ') || undefined,
                            email: payload.holder?.email,
                            phone: payload.holder?.phone,
                            address: {
                                line1: payload.holder?.addressLine,
                                city: payload.holder?.city,
                                state: payload.holder?.stateCode,
                                postal_code: payload.holder?.postalCode,
                                country: payload.holder?.countryCode,
                            },
                        },
                    }
                    : undefined;
                const confirmAction = await dispatch(
                    confirmActivityPayment({clientSecret: response.data.clientSecret, stripe, elements, bookingGuid: response.data.bookingId, paymentDescription, directConfirm} )
                );
                if (confirmActivityPayment.fulfilled.match(confirmAction)) {
                    return confirmAction.payload;
                }
                const errorMsg = (confirmAction.payload as string) || 'Payment confirmation failed';
                Toaster.error(
                    !isSpanish()
                        ? `Booking failed: ${errorMsg}`
                        : `Error en la reserva: ${errorMsg}`
                );
                return rejectWithValue({
                    code: 'PaymentFailed',
                    message: errorMsg,
                });
            }else{
                // From the front's perspective a Pending booking is a success: it was
                // created and the confirmation page renders the "pending supplier
                // confirmation" message. Only genuinely failed statuses should error.
                const bookingStatus = String(response.data.bookingStatus || '').toLowerCase();
                if (bookingStatus !== 'confirmed' && bookingStatus !== 'pending') {
                    const message = response.data.bookingStatusMessage || 'Booking was not confirmed';
                    Toaster.error(
                        !isSpanish()
                            ? `Booking failed: ${message}`
                            : `Error en la reserva: ${message}`
                    );
                    return rejectWithValue({
                        code: 'BookingNotConfirmed',
                        message: message,
                    });
                }
                return response.data;
            }
        } catch (error: any) {
            const code =
                error?.response?.data?.code
                    ? error?.response?.data?.code
                    : error?.response?.data?.message ||
                      error.message ||
                      'An error occurred during booking';

            const message =
                withPaymentErrorDetail(error?.response?.data, error?.response?.data?.reason) ||
                error.message ||
                'An error occurred during booking';

            Toaster.error(
                !isSpanish()
                    ? `Booking failed: ${message}`
                    : `Error en la reserva: ${message}`
            );

            return rejectWithValue({
                status: error.response?.status,
                data: error.response?.data,
                code,
                message,
            });
        }
    }
);

export const getActivityBooking = createAsyncThunk(
    'activityCheckout/getActivityBooking',
    async (_payload: { rateKey: string; activityCode: string }, { rejectWithValue }) => {
        try {
            /*
            const response = await ActivityCheckoutAPI.createBooking(payload);
            if (response.data) {
                return response.data;
            }
            */
            return rejectWithValue('ERROR');
        } catch (error: any) {
            return rejectWithValue('ERROR');
        }
    }
);

const activityCheckoutSlice = createSlice({
    name: 'activityCheckout',
    initialState,
    reducers: {
        resetCheckoutState: (state) => {
            state.loading = false;
            state.error = null;
            state.errorMessages = null;
            state.success = false;
            state.bookingResult = null;
            state.confirmationHolderEmail = null;
            state.bookingAnswers = {};
            state.travelerAnswers = [];
        },
        setBookingAnswer: (state, action: { payload: { questionId: string; value: any; unit?: string } }) => {
            state.bookingAnswers[action.payload.questionId] = {
                value: action.payload.value,
                unit: action.payload.unit
            };
        },
        setTravelerAnswer: (state, action: { payload: { index: number; questionId: string; value: any; unit?: string } }) => {
            if (!state.travelerAnswers[action.payload.index]) {
                state.travelerAnswers[action.payload.index] = {};
            }
            state.travelerAnswers[action.payload.index][action.payload.questionId] = {
                value: action.payload.value,
                unit: action.payload.unit
            };
        },
        initTravelerAnswers: (state, action: { payload: number }) => {
            state.travelerAnswers = new Array(action.payload).fill({}).map(() => ({}));
        },
        setSelectedActivityReviewClientCash: (state, action: PayloadAction<number>) => {
            state.selectedActivityReviewClientCash = action.payload;
        },
        setSelectedActivityReviewClientCashStr: (state, action: PayloadAction<string>) => {
            state.selectedActivityReviewClientCashStr = action.payload;
        },
        setReviewBookRewardId: (state, action: PayloadAction<number | null>) => {
            state.rewardId = action.payload;
        },
        setRebookingError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setRebookingErrorMessages: (state, action: PayloadAction<string | null>) => {
            state.errorMessages = action.payload;
        },
        setStripeLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setLoadingBooking: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setStripeError: (state, action: PayloadAction<string>) => {
            state.errorMessages = action.payload;
        },
        handleProcessBookingComplete: (state, action: PayloadAction<any>) => {
            state.success = true;
            state.bookingResult = action.payload;
        },
        handleErrorBookingComplete: (state, action: PayloadAction<any>) => {
            state.success = false;
            state.errorMessages = action.payload?.message || "Error";
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createActivityBooking.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.errorMessages = null;
                state.success = false;
            })
            .addCase(createActivityBooking.fulfilled, (state, action) => {
                state.loading = false;
                const bookingStatus = String((action.payload as any)?.bookingStatus || '').toLowerCase();
                // Pending counts as success on the front (supplier confirmation still in progress).
                state.success = bookingStatus === 'confirmed' || bookingStatus === 'pending';
                state.bookingResult = action.payload;
                const email = action.meta.arg?.payload?.holder?.email?.trim();
                state.confirmationHolderEmail = email || null;
                if (bookingStatus === 'confirmed') {
                    Toaster.success(!isSpanish() ? 'Booking completed successfully!' : '¡Reserva completada con éxito!');
                }
            })
            .addCase(createActivityBooking.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as any).code;
                state.errorMessages = (action.payload as any).message;
                state.success = false;
            })
            .addCase(getActivityBooking.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.errorMessages = null;
                state.success = false;
            })
            .addCase(getActivityBooking.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.bookingResult = action.payload;
            })
            .addCase(getActivityBooking.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                state.errorMessages = action.payload as string;
                state.success = false;
            });
    },
});

export const {
    resetCheckoutState,
    setBookingAnswer,
    setTravelerAnswer,
    initTravelerAnswers,
    setSelectedActivityReviewClientCash,
    setSelectedActivityReviewClientCashStr,
    setReviewBookRewardId,
    setRebookingError,
    setRebookingErrorMessages,
    setStripeLoading,
    setLoadingBooking,
    setStripeError,
    handleProcessBookingComplete,
    handleErrorBookingComplete,
} = activityCheckoutSlice.actions;

export const activityCheckoutReducer = activityCheckoutSlice.reducer;
